import type { DateValue } from "@/components/shared/formElements/DatePicker";
import { dateValueToString } from "@/components/shared/formElements/DatePicker";
import { BillingFrequency, CalculationCycleStatus } from "@/openapi/generated";

/** UI label for API `cycleDate` — ledger posting date, not the billing period. */
export const TUITION_CYCLE_POST_DATE_LABEL = "Post tuition date";

function isSentinelYmd(ymd: string): boolean {
  return ymd.startsWith("0000-") || ymd.startsWith("0001-");
}

function formatYmdUtc(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** UTC noon so OpenAPI `toISOString().substring(0, 10)` keeps the local calendar day. */
export function toApiDate(ymd: string): Date {
  return new Date(`${ymd}T12:00:00.000Z`);
}

export function toDateInputValue(
  value: Date | string | null | undefined,
): string {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const ymd = value.slice(0, 10);
    return isSentinelYmd(ymd) ? "" : ymd;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  if (date.getUTCFullYear() < 1900 && date.getFullYear() < 1900) {
    return "";
  }
  // Date-only API values become UTC midnight Date objects. Use the UTC
  // calendar day so US timezones do not show the previous local day.
  const ymd = formatYmdUtc(date);
  return isSentinelYmd(ymd) ? "" : ymd;
}

export function formatCycleDate(
  value: Date | string | null | undefined,
): string {
  const ymd = toDateInputValue(value);
  if (!ymd) return "-";
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatCyclePeriodLabel(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
): string | null {
  const start = formatCycleDate(startDate);
  const end = formatCycleDate(endDate);
  if (start === "-" || end === "-") return null;
  return `${start} – ${end}`;
}

export function toCycleRequestDates(values: {
  cycleDate: DateValue;
  startDate: DateValue;
  endDate: DateValue;
}) {
  return {
    cycleDate: toApiDate(dateValueToString(values.cycleDate)),
    startDate: toApiDate(dateValueToString(values.startDate)),
    endDate: toApiDate(dateValueToString(values.endDate)),
  };
}

export function formatCycleStatusLabel(
  status: CalculationCycleStatus | string | null | undefined,
): string {
  switch (status) {
    case CalculationCycleStatus.Pending:
      return "Pending";
    case CalculationCycleStatus.Calculating:
      return "Calculating";
    case CalculationCycleStatus.Calculated:
      return "Calculated";
    case CalculationCycleStatus.CalculationFailed:
      return "Calculation failed";
    case CalculationCycleStatus.Syncing:
      return "Posting";
    case CalculationCycleStatus.Synced:
      return "Synced";
    case CalculationCycleStatus.SyncFailed:
      return "Posting failed";
    default:
      return status ? String(status) : "-";
  }
}

export function getCycleStatusTone(
  status: CalculationCycleStatus | string | null | undefined,
): "neutral" | "success" | "premium" | "danger" | "info" {
  switch (status) {
    case CalculationCycleStatus.Pending:
      return "info";
    case CalculationCycleStatus.Calculating:
    case CalculationCycleStatus.Syncing:
      return "premium";
    case CalculationCycleStatus.Calculated:
    case CalculationCycleStatus.Synced:
      return "success";
    case CalculationCycleStatus.CalculationFailed:
    case CalculationCycleStatus.SyncFailed:
      return "danger";
    default:
      return "neutral";
  }
}

export function formatBillingFrequencyLabel(
  frequency: BillingFrequency | string | null | undefined,
): string {
  switch (frequency) {
    case BillingFrequency.Monthly:
      return "Monthly";
    case BillingFrequency.Weekly:
      return "Weekly";
    case BillingFrequency.Annualy:
      return "Annually";
    default:
      return frequency ? String(frequency) : "-";
  }
}

export const BILLING_FREQUENCY_OPTIONS = [
  { value: BillingFrequency.Monthly, label: "Monthly" },
  { value: BillingFrequency.Weekly, label: "Weekly" },
  { value: BillingFrequency.Annualy, label: "Annually" },
] as const;

export function getCycleFailureMessage(
  status: CalculationCycleStatus | null | undefined,
): string | null {
  if (status === CalculationCycleStatus.CalculationFailed) {
    return "Tuition calculation failed. There is no retry. Close cycle to keep it in history, or Cancel cycle to start over.";
  }
  if (status === CalculationCycleStatus.SyncFailed) {
    return "Posting failed. There is no retry. Close cycle to keep it in history, or Cancel cycle to start over.";
  }
  return null;
}

export function formatCycleLineType(
  lineType: string | null | undefined,
): string {
  if (!lineType) return "-";
  return lineType.replace(/([a-z])([A-Z])/g, "$1 $2");
}
